import cn from "classnames";
import { Text, useMediaQuery } from "../../../../../../packages/ui-kit";
import { useToggleState } from "../../../../../../packages/hooks";
import { FILE_STATUSES } from "../../../../../../packages/constants";
import { CircleLoader } from "../../../../../../packages/components";
import { DangerIcon } from "../../../../../../packages/icons";
import { helpers } from "../../../../../../packages/helpers";
import { DICTIONARY } from "../../../../dictionary";
import { useAppDispatch } from "../../../../../../hooks";
import { getFile } from "../../../../actions";
import { useControlContext } from "../../../../../control";
import { FileIcon } from "./file-icon";
import { FileName } from "./file-name";
import type { MessageContentFile } from "../../../../types";
import styles from "./file.module.scss";

const LOCALE = DICTIONARY.FILE;
const { isDownloading } = helpers.predicates;

type Props = {
  traceId: string;
  content: MessageContentFile;
  dataTestId?: string;
};

const File = ({ dataTestId = "file", traceId, content }: Props) => {
  const { api } = useControlContext();
  const { getFileApi } = api;
  const dispatch = useAppDispatch();
  const {
    toggle: shouldShownDownloadButton,
    toggleOn: showDownloadButton,
    toggleOff: hideDownloadButton,
  } = useToggleState();

  const isMobile = useMediaQuery("mobile");
  const { file } = content;

  const fileStyles = cn(styles.file, {
    [styles.desktop]: !isMobile,
  });

  const handleDownload = () => {
    const params = {
      traceId,
      fileId: content.file.id,
      fileName: content.file.name,
      getFileApi,
    };

    dispatch(getFile(params));
  };

  switch (file.status) {
    case FILE_STATUSES.SUCCESS:
      return (
        <div className={fileStyles} data-test-id={dataTestId}>
          <FileName
            dataTestId={`${dataTestId}-name`}
            name={file.name}
            onClick={handleDownload}
            shouldShownLoader={isDownloading(file.downloading)}
            shouldShownDownloadButton={shouldShownDownloadButton}
            onMouseEnter={showDownloadButton}
            onMouseLeave={hideDownloadButton}
          />
        </div>
      );

    case FILE_STATUSES.ON_CHECK:

    case FILE_STATUSES.CHECKED:
      return (
        <div
          data-test-id={dataTestId}
          className={cn(fileStyles, styles["on-check"])}
        >
          <div className={styles["file-loader-container"]}>
            <CircleLoader className={styles.loader} />
          </div>
          <div className={styles.hidden}>
            <FileName
              dataTestId={`${dataTestId}-name`}
              name={file.name}
              onClick={handleDownload}
            />
          </div>
        </div>
      );

    case FILE_STATUSES.ERROR:
      return (
        <div className={fileStyles} data-test-id={dataTestId}>
          <FileName dataTestId={`${dataTestId}-name`} name={file.name} />

          <div className={styles["error-wrapper"]}>
            <DangerIcon />
            <Text size="xsmall" dataTestId={`${dataTestId}-error`}>
              {LOCALE.ERROR}
            </Text>
          </div>
        </div>
      );

    case FILE_STATUSES.REJECT:

    default:
      return (
        <div className={fileStyles} data-test-id={dataTestId}>
          <FileName
            dataTestId={`${dataTestId}-name`}
            name={file?.name || "file"}
            onClick={handleDownload}
          />
          <div className={cn(styles["file-info"], styles.reject)}>
            <DangerIcon />
            <Text
              size="xsmall"
              className={styles["reject-text"]}
              dataTestId={`${dataTestId}-virus`}
            >
              {LOCALE.VIRUS}
            </Text>
          </div>
        </div>
      );
  }
};

export { File };
